import * as React from "react";
import {Link as RouterLink, useParams, useSearchParams} from "react-router-dom";
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Link,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import {toast} from "react-toastify";
import LoadingDialog from "../../components/loadingDialog";
import {
    AGE_DOB_FIELD_IDS,
    FIELD_TYPES,
    fetchHpDatabaseMapEntries,
    fetchHpEventFieldsDetail,
    fetchHpEventFieldsLookups,
    fetchHpPostEventRandomEntries,
    hpEventFieldsUpsert,
    pairsFromStrings,
    searchHpDatabaseMaps,
    searchHpPostEventRandoms,
} from "./eventFieldsService";

type LookupOpt = {id: number; label: string};

type FieldRowState = {
    key: string;
    postFieldName: string;
    fieldTypeId: number;
    dbFieldNameId: number;
    databaseMapId: number;
    mapPairs: string[];
    mapAddText: string;
    mapSelectedPair: string;
    randomId: number;
    randomPairs: string[];
    randomAddText: string;
    randomSelectedPair: string;
    fixedText: string;
    otherFunctionId: number;
    sort: number;
};

type ViewModal =
    | {kind: "map"; id: number; pairs: Array<{databaseValue: string; postValue: string}>}
    | {kind: "random"; id: number; pairs: Array<{value: string; weight: number}>}
    | null;

type SearchModal = {
    kind: "map" | "random";
    rowKey: string;
} | null;

const denseSx = {
    "& .MuiInputBase-root": {fontSize: 12, minHeight: 28},
    "& .MuiInputBase-input": {py: 0.4, px: 0.75},
    "& .MuiSelect-select": {py: 0.4, px: 0.75},
};

function blankRow(sort = 1): FieldRowState {
    return {
        key: `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        postFieldName: "",
        fieldTypeId: -1,
        dbFieldNameId: -1,
        databaseMapId: 0,
        mapPairs: [],
        mapAddText: "",
        mapSelectedPair: "",
        randomId: 0,
        randomPairs: [],
        randomAddText: "",
        randomSelectedPair: "",
        fixedText: "",
        otherFunctionId: -1,
        sort,
    };
}

function isIntStr(s: string): boolean {
    return /^-?\d+$/.test(String(s).trim());
}

export default function EventFieldsPage() {
    const {eventId: eventIdParam} = useParams();
    const [searchParams] = useSearchParams();

    const eventId = Number(eventIdParam) || 0;
    const hpPostEventId = Number(searchParams.get("hpPostEventId")) || 0;
    const hpPostEventIdCopy = Number(searchParams.get("hpPostEventIdCopy")) || 0;

    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [rows, setRows] = React.useState<FieldRowState[]>([blankRow()]);
    const [dbFields, setDbFields] = React.useState<LookupOpt[]>([]);
    const [otherFunctions, setOtherFunctions] = React.useState<LookupOpt[]>([]);
    const [mapIds, setMapIds] = React.useState<number[]>([]);
    const [randomIds, setRandomIds] = React.useState<number[]>([]);
    const [viewModal, setViewModal] = React.useState<ViewModal>(null);
    const [searchModal, setSearchModal] = React.useState<SearchModal>(null);
    const [searchTerms, setSearchTerms] = React.useState<string[]>(["", "", "", "", ""]);
    const [searchExactOnly, setSearchExactOnly] = React.useState(false);
    const [searchResults, setSearchResults] = React.useState<any[]>([]);
    const [searchBusy, setSearchBusy] = React.useState(false);

    const backEventPath = eventId > 0 ? `/events/${eventId}` : "/events/new";

    const updateRow = (key: string, patch: Partial<FieldRowState>) => {
        setRows((prev) => prev.map((r) => (r.key === key ? {...r, ...patch} : r)));
    };

    const loadMapPairs = async (rowKey: string, mapId: number) => {
        if (mapId < 1) {
            toast.error("No values to load");
            return;
        }
        try {
            const entries = await fetchHpDatabaseMapEntries(mapId);
            const pairs = entries.map((e: any) => `${e.databaseValue}##${e.postValue}`);
            updateRow(rowKey, {mapPairs: pairs, mapSelectedPair: pairs[0] || ""});
        } catch (e: any) {
            toast.error(e?.message || "Failed to load map");
        }
    };

    const loadRandomPairs = async (rowKey: string, randomId: number) => {
        if (randomId < 1) {
            toast.error("No values to load");
            return;
        }
        try {
            const entries = await fetchHpPostEventRandomEntries(randomId);
            const pairs = entries.map((e: any) => `${e.value}##${e.weight}`);
            updateRow(rowKey, {randomPairs: pairs, randomSelectedPair: pairs[0] || ""});
        } catch (e: any) {
            toast.error(e?.message || "Failed to load random");
        }
    };

    const hydrate = React.useCallback(async () => {
        if (!hpPostEventId) {
            toast.error("Missing hpPostEventId");
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const [lookups, detail] = await Promise.all([
                fetchHpEventFieldsLookups(),
                fetchHpEventFieldsDetail(
                    hpPostEventId,
                    hpPostEventIdCopy > 0 ? hpPostEventIdCopy : null
                ),
            ]);
            setDbFields(lookups?.dbFields || []);
            setOtherFunctions(lookups?.otherFunctions || []);
            setMapIds(lookups?.mapIds || []);
            setRandomIds(lookups?.randomIds || []);

            const loaded: FieldRowState[] = await Promise.all(
                (detail?.rows || []).map(async (r: any) => {
                    const row: FieldRowState = {
                        ...blankRow(Number(r.sort) || 1),
                        postFieldName: String(r.postFieldName || ""),
                        fieldTypeId: Number(r.fieldTypeId) || -1,
                        dbFieldNameId: r.dbFieldNameId != null ? Number(r.dbFieldNameId) : -1,
                        databaseMapId: r.databaseMapId != null ? Number(r.databaseMapId) : 0,
                        randomId: r.randomId != null ? Number(r.randomId) : 0,
                        fixedText: String(r.fixedText || ""),
                        otherFunctionId:
                            r.otherFunctionId != null ? Number(r.otherFunctionId) : -1,
                        sort: Number(r.sort) || 1,
                    };
                    // Auto-load existing map/random entries (legacy loadInitValues)
                    if (row.databaseMapId > 0) {
                        try {
                            const entries = await fetchHpDatabaseMapEntries(row.databaseMapId);
                            row.mapPairs = entries.map(
                                (e: any) => `${e.databaseValue}##${e.postValue}`
                            );
                            row.mapSelectedPair = row.mapPairs[0] || "";
                        } catch {
                            /* ignore empty */
                        }
                    }
                    if (row.randomId > 0) {
                        try {
                            const entries = await fetchHpPostEventRandomEntries(row.randomId);
                            row.randomPairs = entries.map(
                                (e: any) => `${e.value}##${e.weight}`
                            );
                            row.randomSelectedPair = row.randomPairs[0] || "";
                        } catch {
                            /* ignore empty */
                        }
                    }
                    return row;
                })
            );
            loaded.push(blankRow(1));
            setRows(loaded);
        } catch (e: any) {
            toast.error(e?.message || "Failed to load event fields");
        } finally {
            setLoading(false);
        }
    }, [hpPostEventId, hpPostEventIdCopy]);

    React.useEffect(() => {
        hydrate();
    }, [hydrate]);

    const onTypeChange = (key: string, fieldTypeId: number, isLast: boolean) => {
        updateRow(key, {fieldTypeId});
        if (isLast && fieldTypeId > 0) {
            setRows((prev) => [...prev, blankRow(1)]);
        }
    };

    const addMapPair = (row: FieldRowState) => {
        const raw = String(row.mapAddText || "").trim();
        const parts = raw.split("##");
        if (parts.length !== 2) {
            if (AGE_DOB_FIELD_IDS.has(row.dbFieldNameId)) {
                toast.error(`Must be formatted younger_age##older_age, length:${parts.length}`);
            } else {
                toast.error(`Must be formatted lead_value##post_value, length:${parts.length}`);
            }
            return;
        }
        if (AGE_DOB_FIELD_IDS.has(row.dbFieldNameId)) {
            if (!isIntStr(parts[0]) || !isIntStr(parts[1])) {
                toast.error("Non numeric Age/DOB fields, must be formatted younger_age##older_age");
                return;
            }
            if (parseInt(parts[0], 10) > parseInt(parts[1], 10)) {
                toast.error(
                    `Younger age (${parts[0]}) should be less than or equal to older age (${parts[1]})`
                );
                return;
            }
        }
        if (row.mapPairs.some((p) => p.split("##")[0] === parts[0])) {
            toast.error(`Invalid duplicate value: ${parts[0]}.`);
            return;
        }
        updateRow(row.key, {
            mapPairs: [...row.mapPairs, raw],
            mapAddText: "",
            mapSelectedPair: raw,
        });
    };

    const removeMapPair = (row: FieldRowState) => {
        if (!row.mapSelectedPair) {
            return;
        }
        const next = row.mapPairs.filter((p) => p !== row.mapSelectedPair);
        updateRow(row.key, {
            mapPairs: next,
            mapSelectedPair: next[0] || "",
        });
    };

    const addRandomPair = (row: FieldRowState) => {
        const raw = String(row.randomAddText || "").trim();
        const parts = raw.split("##");
        if (parts.length !== 2) {
            toast.error(`Must be formatted value##weight, length:${parts.length}`);
            return;
        }
        if (!isIntStr(parts[1])) {
            toast.error(`Invalid weight: ${parts[1]}.`);
            return;
        }
        if (row.randomPairs.some((p) => p.split("##")[0] === parts[0])) {
            toast.error(`Invalid duplicate value: ${parts[0]}.`);
            return;
        }
        updateRow(row.key, {
            randomPairs: [...row.randomPairs, raw],
            randomAddText: "",
            randomSelectedPair: raw,
        });
    };

    const removeRandomPair = (row: FieldRowState) => {
        if (!row.randomSelectedPair) {
            return;
        }
        const next = row.randomPairs.filter((p) => p !== row.randomSelectedPair);
        updateRow(row.key, {
            randomPairs: next,
            randomSelectedPair: next[0] || "",
        });
    };

    const openViewMap = async (mapId: number) => {
        if (mapId < 1) {
            toast.error("No values to view");
            return;
        }
        try {
            const pairs = await fetchHpDatabaseMapEntries(mapId);
            setViewModal({kind: "map", id: mapId, pairs});
        } catch (e: any) {
            toast.error(e?.message || "Failed to view map");
        }
    };

    const openViewRandom = async (randomId: number) => {
        if (randomId < 1) {
            toast.error("No values to view");
            return;
        }
        try {
            const pairs = await fetchHpPostEventRandomEntries(randomId);
            setViewModal({kind: "random", id: randomId, pairs});
        } catch (e: any) {
            toast.error(e?.message || "Failed to view random");
        }
    };

    const runSearch = async () => {
        if (!searchModal) {
            return;
        }
        const terms = searchTerms.map((t) => t.trim()).filter(Boolean);
        if (!terms.length) {
            toast.error("Enter at least one search term");
            return;
        }
        setSearchBusy(true);
        try {
            if (searchModal.kind === "map") {
                setSearchResults(await searchHpDatabaseMaps(terms, searchExactOnly));
            } else {
                setSearchResults(await searchHpPostEventRandoms(terms, searchExactOnly));
            }
        } catch (e: any) {
            toast.error(e?.message || "Search failed");
        } finally {
            setSearchBusy(false);
        }
    };

    const applySearchId = async (id: number) => {
        if (!searchModal) {
            return;
        }
        if (searchModal.kind === "map") {
            updateRow(searchModal.rowKey, {databaseMapId: id});
            await loadMapPairs(searchModal.rowKey, id);
        } else {
            updateRow(searchModal.rowKey, {randomId: id});
            await loadRandomPairs(searchModal.rowKey, id);
        }
        setSearchModal(null);
        setSearchResults([]);
    };

    const save = async () => {
        if (!hpPostEventId) {
            toast.error("Missing hpPostEventId");
            return;
        }
        setSaving(true);
        try {
            const payloadRows = rows
                .filter((r) => r.fieldTypeId > 0)
                .map((r) => ({
                    postFieldName: String(r.postFieldName || "").trim(),
                    fieldTypeId: Number(r.fieldTypeId),
                    dbFieldNameId: r.dbFieldNameId > 0 ? r.dbFieldNameId : null,
                    databaseMapId: r.databaseMapId,
                    mapPairs:
                        r.databaseMapId === -1 ? pairsFromStrings(r.mapPairs) : null,
                    randomId: r.randomId,
                    randomPairs: r.randomId === -1 ? pairsFromStrings(r.randomPairs) : null,
                    fixedText: r.fixedText || "",
                    otherFunctionId: r.otherFunctionId > 0 ? r.otherFunctionId : null,
                    sort: Number(r.sort) || 0,
                }));

            await hpEventFieldsUpsert({hpPostEventId, rows: payloadRows});
            toast.success("Fields saved");
            // Refresh lookups (new map/random ids) and rows
            await hydrate();
        } catch (e: any) {
            toast.error(e?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const sortOptions = React.useMemo(() => Array.from({length: 100}, (_, i) => i), []);

    if (!hpPostEventId) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">Missing hpPostEventId query parameter.</Typography>
                <Link component={RouterLink} to={backEventPath}>
                    Back to Event
                </Link>
            </Box>
        );
    }

    if (loading) {
        return <LoadingDialog />;
    }

    return (
        <Box sx={{p: 1.5, maxWidth: "100%", overflowX: "auto"}}>
            {saving && <LoadingDialog />}
            <Box sx={{display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap"}}>
                <Link component={RouterLink} to={backEventPath} sx={{fontSize: 14}}>
                    Back to Event
                </Link>
                <Typography sx={{fontSize: 16, fontWeight: 700}}>
                    Post Event Fields [Event {eventId}][Fields {hpPostEventId}]
                    {hpPostEventIdCopy > 0 ? ` (copy from ${hpPostEventIdCopy})` : ""}
                </Typography>
            </Box>
            <Typography sx={{fontSize: 12, mb: 1.5}}>
                Notes:{" "}
                <Box component="span" sx={{color: "error.main"}}>
                    xxAccountId, lp_campaign_key, APIKey
                </Box>{" "}
                go in the campaign page not in this event page.
            </Typography>

            <Table size="small" sx={{"& td, & th": {verticalAlign: "top", px: 0.5, py: 0.75}}}>
                <TableHead>
                    <TableRow>
                        <TableCell>Post Field Name</TableCell>
                        <TableCell>Type of Value</TableCell>
                        <TableCell>Lead Field</TableCell>
                        <TableCell>Lead Field → Map</TableCell>
                        <TableCell>Random</TableCell>
                        <TableCell>Fixed Text</TableCell>
                        <TableCell>Other Function</TableCell>
                        <TableCell>Order</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, idx) => {
                        const isLast = idx === rows.length - 1;
                        return (
                            <TableRow key={row.key}>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        value={row.postFieldName}
                                        onChange={(e) =>
                                            updateRow(row.key, {
                                                postFieldName: e.target.value.slice(0, 100),
                                            })
                                        }
                                        inputProps={{maxLength: 100}}
                                        sx={{...denseSx, width: 130}}
                                    />
                                </TableCell>
                                <TableCell>
                                    <FormControl size="small" sx={{...denseSx, minWidth: 140}}>
                                        <Select
                                            value={row.fieldTypeId}
                                            onChange={(e) =>
                                                onTypeChange(
                                                    row.key,
                                                    Number(e.target.value),
                                                    isLast
                                                )
                                            }
                                        >
                                            {FIELD_TYPES.map((t) => (
                                                <MenuItem key={t.id} value={t.id}>
                                                    {t.label || "\u00A0"}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <FormControl size="small" sx={{...denseSx, minWidth: 140}}>
                                        <Select
                                            value={row.dbFieldNameId}
                                            onChange={(e) =>
                                                updateRow(row.key, {
                                                    dbFieldNameId: Number(e.target.value),
                                                })
                                            }
                                        >
                                            <MenuItem value={-1}>{"\u00A0"}</MenuItem>
                                            {dbFields.map((f) => (
                                                <MenuItem key={f.id} value={f.id}>
                                                    {f.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell sx={{minWidth: 220}}>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                        <FormControl size="small" sx={{...denseSx, minWidth: 80}}>
                                            <Select
                                                value={row.databaseMapId}
                                                onChange={(e) =>
                                                    updateRow(row.key, {
                                                        databaseMapId: Number(e.target.value),
                                                        mapPairs: [],
                                                        mapSelectedPair: "",
                                                    })
                                                }
                                            >
                                                <MenuItem value={0}>None</MenuItem>
                                                <MenuItem value={-1}>NEW</MenuItem>
                                                {mapIds.map((id) => (
                                                    <MenuItem key={id} value={id}>
                                                        {id}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Typography sx={{fontSize: 11}}>
                                            <Link
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    loadMapPairs(row.key, row.databaseMapId)
                                                }
                                            >
                                                load
                                            </Link>
                                            /
                                            <Link
                                                component="button"
                                                type="button"
                                                onClick={() => openViewMap(row.databaseMapId)}
                                            >
                                                view
                                            </Link>
                                            /
                                            <Link
                                                component="button"
                                                type="button"
                                                onClick={() => {
                                                    setSearchModal({
                                                        kind: "map",
                                                        rowKey: row.key,
                                                    });
                                                    setSearchTerms(["", "", "", "", ""]);
                                                    setSearchExactOnly(false);
                                                    setSearchResults([]);
                                                }}
                                            >
                                                search
                                            </Link>
                                        </Typography>
                                    </Box>
                                    <Box sx={{display: "flex", gap: 0.5, mt: 0.5}}>
                                        <TextField
                                            size="small"
                                            value={row.mapAddText}
                                            onChange={(e) =>
                                                updateRow(row.key, {
                                                    mapAddText: e.target.value.slice(0, 200),
                                                })
                                            }
                                            sx={{...denseSx, flex: 1}}
                                        />
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => addMapPair(row)}
                                            sx={{minWidth: 32, px: 0.5}}
                                        >
                                            +
                                        </Button>
                                    </Box>
                                    <Box sx={{display: "flex", gap: 0.5, mt: 0.5}}>
                                        <FormControl size="small" sx={{...denseSx, flex: 1}}>
                                            <Select
                                                value={row.mapSelectedPair}
                                                onChange={(e) =>
                                                    updateRow(row.key, {
                                                        mapSelectedPair: String(e.target.value),
                                                    })
                                                }
                                                displayEmpty
                                            >
                                                {row.mapPairs.map((p) => (
                                                    <MenuItem key={p} value={p}>
                                                        {p}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => removeMapPair(row)}
                                            sx={{minWidth: 32, px: 0.5}}
                                        >
                                            -
                                        </Button>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{minWidth: 220}}>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                        <FormControl size="small" sx={{...denseSx, minWidth: 80}}>
                                            <Select
                                                value={row.randomId}
                                                onChange={(e) =>
                                                    updateRow(row.key, {
                                                        randomId: Number(e.target.value),
                                                        randomPairs: [],
                                                        randomSelectedPair: "",
                                                    })
                                                }
                                            >
                                                <MenuItem value={0}>None</MenuItem>
                                                <MenuItem value={-1}>NEW</MenuItem>
                                                {randomIds.map((id) => (
                                                    <MenuItem key={id} value={id}>
                                                        {id}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Typography sx={{fontSize: 11}}>
                                            <Link
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    loadRandomPairs(row.key, row.randomId)
                                                }
                                            >
                                                load
                                            </Link>
                                            /
                                            <Link
                                                component="button"
                                                type="button"
                                                onClick={() => openViewRandom(row.randomId)}
                                            >
                                                view
                                            </Link>
                                            /
                                            <Link
                                                component="button"
                                                type="button"
                                                onClick={() => {
                                                    setSearchModal({
                                                        kind: "random",
                                                        rowKey: row.key,
                                                    });
                                                    setSearchTerms(["", "", "", "", ""]);
                                                    setSearchExactOnly(false);
                                                    setSearchResults([]);
                                                }}
                                            >
                                                search
                                            </Link>
                                        </Typography>
                                    </Box>
                                    <Box sx={{display: "flex", gap: 0.5, mt: 0.5}}>
                                        <TextField
                                            size="small"
                                            value={row.randomAddText}
                                            onChange={(e) =>
                                                updateRow(row.key, {
                                                    randomAddText: e.target.value.slice(0, 200),
                                                })
                                            }
                                            sx={{...denseSx, flex: 1}}
                                        />
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => addRandomPair(row)}
                                            sx={{minWidth: 32, px: 0.5}}
                                        >
                                            +
                                        </Button>
                                    </Box>
                                    <Box sx={{display: "flex", gap: 0.5, mt: 0.5}}>
                                        <FormControl size="small" sx={{...denseSx, flex: 1}}>
                                            <Select
                                                value={row.randomSelectedPair}
                                                onChange={(e) =>
                                                    updateRow(row.key, {
                                                        randomSelectedPair: String(
                                                            e.target.value
                                                        ),
                                                    })
                                                }
                                                displayEmpty
                                            >
                                                {row.randomPairs.map((p) => (
                                                    <MenuItem key={p} value={p}>
                                                        {p}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => removeRandomPair(row)}
                                            sx={{minWidth: 32, px: 0.5}}
                                        >
                                            -
                                        </Button>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        value={row.fixedText}
                                        onChange={(e) =>
                                            updateRow(row.key, {
                                                fixedText: e.target.value.slice(0, 400),
                                            })
                                        }
                                        inputProps={{maxLength: 400}}
                                        sx={{...denseSx, width: 140}}
                                    />
                                </TableCell>
                                <TableCell>
                                    <FormControl size="small" sx={{...denseSx, minWidth: 130}}>
                                        <Select
                                            value={row.otherFunctionId}
                                            onChange={(e) =>
                                                updateRow(row.key, {
                                                    otherFunctionId: Number(e.target.value),
                                                })
                                            }
                                        >
                                            <MenuItem value={-1}>{"\u00A0"}</MenuItem>
                                            {otherFunctions.map((f) => (
                                                <MenuItem key={f.id} value={f.id}>
                                                    {f.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <FormControl size="small" sx={{...denseSx, minWidth: 64}}>
                                        <Select
                                            value={row.sort}
                                            onChange={(e) =>
                                                updateRow(row.key, {
                                                    sort: Number(e.target.value),
                                                })
                                            }
                                        >
                                            {sortOptions.map((n) => (
                                                <MenuItem key={n} value={n}>
                                                    {n}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <Box sx={{mt: 2}}>
                <Button
                    variant="contained"
                    onClick={save}
                    disabled={saving || loading}
                    sx={{minWidth: 150, height: 44, fontSize: 18}}
                >
                    Submit
                </Button>
            </Box>

            <Dialog open={!!viewModal} onClose={() => setViewModal(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {viewModal?.kind === "map"
                        ? `Map ID: ${viewModal.id}`
                        : viewModal
                          ? `Random ID: ${viewModal.id}`
                          : ""}
                </DialogTitle>
                <DialogContent>
                    {viewModal?.kind === "map" && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Database Value</TableCell>
                                    <TableCell>Post Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {viewModal.pairs.map((p, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{p.databaseValue}</TableCell>
                                        <TableCell>{p.postValue}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                    {viewModal?.kind === "random" && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Value</TableCell>
                                    <TableCell>Weight</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {viewModal.pairs.map((p, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{p.value}</TableCell>
                                        <TableCell>{p.weight}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={!!searchModal}
                onClose={() => setSearchModal(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Search</DialogTitle>
                <DialogContent>
                    <Typography sx={{fontSize: 12, mb: 1}}>
                        Up to 5 terms (exact match on{" "}
                        {searchModal?.kind === "map" ? "post_value" : "value"}). Click an ID to
                        select it.
                    </Typography>
                    <RadioGroup
                        row
                        value={searchExactOnly ? "1" : "0"}
                        onChange={(e) => setSearchExactOnly(e.target.value === "1")}
                    >
                        <FormControlLabel
                            value="0"
                            control={<Radio size="small" />}
                            label="Any (contains all terms)"
                        />
                        <FormControlLabel
                            value="1"
                            control={<Radio size="small" />}
                            label="Exact (only these terms)"
                        />
                    </RadioGroup>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, my: 1}}>
                        {searchTerms.map((t, i) => (
                            <TextField
                                key={i}
                                size="small"
                                label={`Term ${i + 1}`}
                                value={t}
                                onChange={(e) => {
                                    const next = [...searchTerms];
                                    next[i] = e.target.value;
                                    setSearchTerms(next);
                                }}
                                sx={{...denseSx, width: 140}}
                            />
                        ))}
                        <Button
                            variant="contained"
                            size="small"
                            onClick={runSearch}
                            disabled={searchBusy}
                        >
                            Search
                        </Button>
                    </Box>
                    {!searchResults.length ? (
                        <Typography sx={{fontSize: 13}}>No mappings found.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>
                                        {searchModal?.kind === "map"
                                            ? "Database Value"
                                            : "Weight"}
                                    </TableCell>
                                    <TableCell>
                                        {searchModal?.kind === "map" ? "Post Value" : "Value"}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {searchResults.map((r: any, i: number) => {
                                    const id =
                                        searchModal?.kind === "map"
                                            ? Number(r.mapId)
                                            : Number(r.randomId);
                                    return (
                                        <TableRow
                                            key={i}
                                            hover
                                            sx={{cursor: "pointer"}}
                                            onClick={() => applySearchId(id)}
                                        >
                                            <TableCell>{id}</TableCell>
                                            {searchModal?.kind === "map" ? (
                                                <>
                                                    <TableCell>{r.databaseValue}</TableCell>
                                                    <TableCell>{r.postValue}</TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell>{r.weight}</TableCell>
                                                    <TableCell>{r.value}</TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
